/**
 * NAT HOF — GTM dataLayer Event Helpers
 * ======================================
 * All events are pushed to window.dataLayer for GTM to capture.
 * NO direct gtag calls. GTM handles GA4, Google Ads, Meta, TikTok, etc.
 *
 * Debug: every push is logged to console in dev or when debug flag is set.
 */

// ─── ID Helpers ──────────────────────────────────────────────────────────────
/** Extrai o id numérico de um GID do Shopify. 'gid://shopify/ProductVariant/47312743104699' -> '47312743104699'. Se já for numérico/limpo, retorna como está. */
export function shopifyNumericId(gid: string | undefined | null): string {
  if (!gid) return '';
  return gid.includes('/') ? (gid.split('/').pop() || '') : gid;
}
/** Monta o id do Google Merchant: shopify_ZZ_<productId>_<variantId> (ambos numéricos). */
export function buildMerchantItemId(productGid: string, variantGid: string): string {
  const pid = shopifyNumericId(productGid);
  const vid = shopifyNumericId(variantGid);
  return `shopify_ZZ_${pid}_${vid}`;
}

// ─── Types ──────────────────────────────────────────────────────────────────

export interface EcommerceItem {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category?: string;
  item_variant?: string;
  /** Variant ID numérico puro — usado SOMENTE para content_ids do Meta (catálogo Shopify). GA4 ignora. */
  item_variant_id?: string;
  price: number;
  currency?: string;
  quantity: number;
  item_list_name?: string;
  index?: number;
}

// ─── Core push ──────────────────────────────────────────────────────────────

function push(event: string, data: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];

  // Clear previous ecommerce object to avoid merging (GTM best-practice)
  if (data.ecommerce) {
    window.dataLayer.push({ ecommerce: null });
  }

  // event_id is ALWAYS present so GTM's data model never leaks a previous
  // event's ID into the next one (Meta Pixel <-> CAPI dedup key).
  const payload = { event, ...data, event_id: (data.event_id as string | undefined) ?? null };
  window.dataLayer.push(payload);

  // Debug log
  if (import.meta.env.DEV || sessionStorage.getItem('gtm_debug') === '1') {
    console.log('GTM EVENT:', event, data);
  }
}

// ─── Ecommerce Events (GA4 schema) ─────────────────────────────────────────

export function dlViewItemList(
  listName: string,
  items: EcommerceItem[],
): void {
  push('view_item_list', {
    ecommerce: {
      item_list_name: listName,
      items: items.map((i, idx) => ({ ...i, currency: i.currency || 'BRL', index: i.index ?? idx })),
    },
  });
}

export function dlSelectItem(item: EcommerceItem, listName?: string): void {
  push('select_item', {
    ecommerce: {
      item_list_name: listName,
      items: [{ ...item, currency: item.currency || 'BRL' }],
    },
  });
}

export function dlViewItem(item: EcommerceItem, eventId?: string): void {
  const safePrice = Number.isFinite(item.price) ? item.price : 0;
  const safeQty = Number.isFinite(item.quantity) && item.quantity > 0 ? item.quantity : 1;
  const rawValue = safePrice * safeQty;
  const value = Number.isFinite(rawValue) ? rawValue : 0;
  if (!Number.isFinite(item.price) && import.meta.env.DEV) {
    console.warn('[dlViewItem] non-finite price coerced to 0', item);
  }
  push('view_item', {
    ...(eventId ? { event_id: eventId } : {}),
    ecommerce: {
      currency: item.currency || 'BRL',
      value,
      items: [{ item_brand: 'NAT HOF', ...item, price: safePrice, quantity: safeQty, currency: item.currency || 'BRL' }],
    },
  });
}

export function dlAddToCart(items: EcommerceItem[], eventId?: string): void {
  const rawValue = items.reduce((s, i) => s + (Number.isFinite(i.price) ? i.price : 0) * (Number.isFinite(i.quantity) ? i.quantity : 1), 0);
  const value = Number.isFinite(rawValue) ? rawValue : 0;
  push('add_to_cart', {
    ...(eventId ? { event_id: eventId } : {}),
    ecommerce: {
      currency: 'BRL',
      value,
      items: items.map(i => ({ ...i, currency: i.currency || 'BRL' })),
    },
  });
}

export function dlRemoveFromCart(items: EcommerceItem[]): void {
  const value = items.reduce((s, i) => s + i.price * i.quantity, 0);
  push('remove_from_cart', {
    ecommerce: {
      currency: 'BRL',
      value,
      items: items.map(i => ({ ...i, currency: i.currency || 'BRL' })),
    },
  });
}

export function dlViewCart(items: EcommerceItem[], value: number): void {
  push('view_cart', {
    ecommerce: {
      currency: 'BRL',
      value,
      items: items.map(i => ({ ...i, currency: i.currency || 'BRL' })),
    },
  });
}

export function dlBeginCheckout(items: EcommerceItem[], value: number, eventId?: string): void {
  // The GA4 begin_checkout dupe was resolved in GTM (GA4 tag paused in v41).
  // Restore the original event name so the same GTM custom event also feeds
  // Google Ads, Facebook InitiateCheckout and TikTok InitiateCheckout tags.
  push('begin_checkout', {
    ...(eventId ? { event_id: eventId } : {}),
    ecommerce: {
      currency: 'BRL',
      value,
      items: items.map(i => ({ ...i, currency: i.currency || 'BRL' })),
    },
  });
}

/**
 * Cotação de frete na PDP — NÃO é o passo de entrega do checkout.
 * `add_shipping_info` é reservado para o checkout e é medido pelo Custom Pixel
 * da Shopify (`checkout_shipping_info_submitted`). Usar o nome reservado aqui
 * inflava o funil do GA4 (224 eventos / 32 usuários).
 */
export function dlCalculateShipping(
  items: EcommerceItem[],
  value: number,
  shippingTier?: string,
): void {
  push('calculate_shipping', {
    ecommerce: {
      currency: 'BRL',
      value,
      shipping_tier: shippingTier || 'standard',
      items: items.map(i => ({ ...i, currency: i.currency || 'BRL' })),
    },
  });
}

export function dlAddPaymentInfo(items: EcommerceItem[], value: number, paymentType?: string): void {
  push('add_payment_info', {
    ecommerce: {
      currency: 'BRL',
      value,
      payment_type: paymentType,
      items: items.map(i => ({ ...i, currency: i.currency || 'BRL' })),
    },
  });
}

export function dlPurchase(
  transactionId: string,
  value: number,
  items: EcommerceItem[],
  extra?: { tax?: number; shipping?: number; coupon?: string; eventId?: string },
): void {
  push('purchase', {
    // event_id enables Meta browser↔server dedup. The server webhook
    // (order-paid-tracking) emits the SAME id (purchase_<order_number>).
    ...(extra?.eventId ? { event_id: extra.eventId } : {}),
    ecommerce: {
      transaction_id: transactionId,
      currency: 'BRL',
      value,
      tax: extra?.tax ?? 0,
      shipping: extra?.shipping ?? 0,
      coupon: extra?.coupon,
      items: items.map(i => ({ ...i, currency: i.currency || 'BRL' })),
    },
  });
}

// ─── UX Events ──────────────────────────────────────────────────────────────

export function dlHeroCtaClick(location: string, slideId?: string): void {
  push('hero_cta_click', { location, slide_id: slideId });
}

export function dlProductCardClick(itemId: string, itemName: string, listName?: string): void {
  push('product_card_click', { item_id: itemId, item_name: itemName, list_name: listName });
}

export function dlIngredientCardClick(ingredientName: string): void {
  push('ingredient_card_click', { ingredient_name: ingredientName });
}

export function dlFaqExpand(question: string, location?: string): void {
  push('faq_expand', { question, location });
}

export function dlNewsletterSignup(method?: string): void {
  push('newsletter_signup', { method });
}

export function dlWhatsAppClick(location: string): void {
  push('whatsapp_click', { location });
}

/** Lead magnet / form submission. method defaults to 'lead_magnet'. */
export function dlGenerateLead(material: string, method = 'lead_magnet'): void {
  push('generate_lead', { material, method });
}

/**
 * Kit configurator intent — fired when the user clicks the kit CTA
 * ("Montar minha rotina"). Separate from add_to_cart so kit interest is
 * measurable even before the kit is added to the cart.
 */
export function dlKitBuilderStart(kit: { id?: string; name: string; value?: number }): void {
  push('kit_builder_start', {
    kit_id: kit.id,
    kit_name: kit.name,
    value: kit.value,
    currency: 'BRL',
  });
}

export function dlScrollDepth(depthPct: number, route: string): void {
  push('scroll_depth', { depth_pct: depthPct, route });
}

// ─── Article Engagement Events ──────────────────────────────────────────────

export function dlArticleRead(articleTitle: string, articlePath: string): void {
  push('article_read', { article_title: articleTitle, article_path: articlePath });
}

export function dlArticleScroll50(articleTitle: string, articlePath: string): void {
  push('article_scroll_50', { article_title: articleTitle, article_path: articlePath });
}

export function dlArticleScroll90(articleTitle: string, articlePath: string): void {
  push('article_scroll_90', { article_title: articleTitle, article_path: articlePath });
}

export function dlInternalLinkClick(from: string, to: string, linkText?: string): void {
  push('internal_link_click', { from, to, link_text: linkText });
}

// ─── Page View (virtual, for SPA) ──────────────────────────────────────────

export function dlPageView(path: string, title: string): void {
  push('page_view', { page_path: path, page_title: title });
}


// ─── Helper: build EcommerceItem from Shopify product node ──────────────────

export function shopifyNodeToItem(
  node: { id: string; handle: string; title: string; productType?: string },
  price: number,
  quantity: number,
  listName?: string,
  index?: number,
  variantId?: string,
): EcommerceItem {
  const hasVariant = !!variantId;
  return {
    item_id: hasVariant ? buildMerchantItemId(node.id, variantId!) : (node.handle || node.id),
    item_name: node.title,
    item_brand: 'NAT HOF',
    item_category: node.productType || 'dermocosmético',
    price,
    quantity,
    currency: 'BRL',
    item_list_name: listName,
    index,
    ...(hasVariant ? { item_variant_id: shopifyNumericId(variantId!) } : {}),
  };
}

/** "Avise-me quando chegar" signup. Never carries e-mail/phone. */
export function dlBackInStockSignup(item: { item_id: string; item_name: string }): void {
  push('back_in_stock_signup', {
    currency: 'BRL',
    items: [{ item_id: item.item_id, item_name: item.item_name }],
    item_id: item.item_id,
    item_name: item.item_name,
  });
}
